<template>
  <div class="space-y-4">
    <div class="bg-white shadow overflow-hidden sm:rounded-md">
      <div class="px-4 py-5 sm:p-6">
        <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
          Optimization Recommendations
        </h3>

        <div class="space-y-4">
          <div
            v-for="rec in recommendations"
            :key="rec.key"
            class="border-l-4 pl-4 pb-4"
            :class="getRecommendationClass(rec.key)">
            <div class="flex items-start justify-between">
              <div class="flex-1 pr-4">
                <label class="text-sm font-medium text-gray-700">
                  {{ rec.title }}
                </label>
                <p class="text-sm text-gray-600 mt-1">
                  {{ rec.message }}
                </p>
              </div>
              <div class="flex items-center space-x-3 flex-shrink-0">
                <label class="flex items-center cursor-pointer">
                  <input
                    :id="`${rec.key}_yes`"
                    :name="rec.key"
                    type="radio"
                    :value="true"
                    :checked="localRecommendations[rec.key] === true || localRecommendations[rec.key] === 'true'"
                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                    @change="setRecommendationValue(rec.key, true)">
                  <span class="ml-2 text-sm font-medium text-gray-700">Yes</span>
                </label>
                <label class="flex items-center cursor-pointer">
                  <input
                    :id="`${rec.key}_no`"
                    :name="rec.key"
                    type="radio"
                    :value="false"
                    :checked="localRecommendations[rec.key] === false || localRecommendations[rec.key] === 'false'"
                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                    @change="setRecommendationValue(rec.key, false)">
                  <span class="ml-2 text-sm font-medium text-gray-700">No</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['update:modelValue'])

// Recommendations configuration
const recommendations = [
  {
    key: 'needs_more_photos',
    title: 'More Photos Needed',
    message: 'Your profile should have more photos. We will continue to provide you with Check-Ins which will add' +
      ' more photos to your profile over time but we recommend you add some here when possible as well.'
  },
  {
    key: 'keywords_violation',
    title: 'Keywords in Name',
    message: 'You are using keywords in your profile name. This is against Google\'s terms. Use only your legal business name.'
  },
  {
    key: 'category_change',
    title: 'Category Optimization',
    message: 'We recommend a different category for your profile. After doing some research, we think a different category is best.'
  },
  {
    key: 'missing_about',
    title: 'Missing About Section',
    message: 'Your "About" section is missing. We recommend you add to this section.'
  },
  {
    key: 'residential_address',
    title: 'Residential Address Issue',
    message: 'Your address is a residential address. This poses a security risk. We highly recommend you use a' +
      ' commercial address or remove the address and use service areas instead.'
  },
  {
    key: 'private_mailbox',
    title: 'Private Mailbox Address',
    message: 'Your address is a private mailbox location. This goes against Google\'s terms. We highly recommend' +
      ' you use a commercial address or remove the address and use service areas instead.'
  },
  {
    key: 'coworking_space',
    title: 'Co-working Space Address',
    message: 'Your address is a co-working space location. This may go against Google\'s terms. We highly' +
      ' recommend you use a commercial address or remove the address and use service areas instead.'
  },
  {
    key: 'franchise_url',
    title: 'Franchise URL Issue',
    message: 'Your business is a franchise. We recommend the homepage go to your direct URL, not the main franchise URL.'
  },
  {
    key: 'twenty_four_hours_issue',
    title: '24-Hour Display Issue',
    message: 'Your regular hours are showing as 24 hours. If you pick up the phone at any time then disregard' +
      ' this item. Otherwise use your regular hours here.'
  },
  {
    key: 'appointment_link',
    title: 'Appointment Link Issue',
    message: 'Your profile shows an appointment link that goes to a contact page.' +
      ' We recommend using an actual appointment setting service (such as Calendly or TidyCal) or remove this link.'
  },
  {
    key: 'phone_mismatch',
    title: 'Phone Number Mismatch',
    message: 'The phone number on your profile does not match the number on your website.' +
      ' We recommend using the same number on both.'
  },
  {
    key: 'needs_reviews',
    title: 'Insufficient Reviews',
    message: 'You do not have enough reviews for Google to recommend you.' +
      ' Strive to hit your first 25 as Google will begin to recommend after you receive 15 - 25 reviews.'
  },
  {
    key: 'not_responding_reviews',
    title: 'Not Responding to Reviews',
    message: 'You are not responding to your reviews. We recommend you always respond to reviews, including negative ones.'
  },
  {
    key: 'great_reviews',
    title: 'Great Review Performance',
    message: 'Great job on reviews! Continue asking for and responding to them, including the negative ones.'
  },
  {
    key: 'unanswered_questions',
    title: 'Unanswered Questions',
    message: 'You are not responding to some of your questions.' +
      ' Use this as a means to answer questions the public may have about your business.'
  }
]

// Local copy
const localRecommendations = ref({ ...props.modelValue })

// Methods
const updateRecommendations = () => {
  emit('update:modelValue', { ...localRecommendations.value })
}

const setRecommendationValue = (key, value) => {
  localRecommendations.value[key] = value
  updateRecommendations()
}

const getRecommendationClass = (key) => {
  const value = localRecommendations.value[key]
  if (value === true || value === 'true') {
    return 'border-yellow-400 bg-yellow-50'
  }
  return 'border-gray-200'
}

// Watch for external changes
watch(() => props.modelValue, (newValue) => {
  localRecommendations.value = { ...newValue }
}, { deep: true })
</script>
